import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Copy-to-clipboard with a self-clearing "copied" flag.
 *
 * `navigator.clipboard` is unavailable outside a secure context — which includes a plain-HTTP
 * dev server on a phone, the exact setup someone will test the address card on — so the
 * `execCommand` path stays as a fallback. It is deprecated and works everywhere that matters.
 *
 * The flag resets itself so the button returns to its resting label; the timer is cleared on
 * unmount to avoid setting state on a card the user has already navigated away from.
 */
export type CopyStatus = 'idle' | 'copied' | 'failed';

export function useCopyToClipboard(resetAfterMs = 2000) {
  const [status, setStatus] = useState<CopyStatus>('idle');
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copy = useCallback(
    async (text: string): Promise<boolean> => {
      const ok = await writeText(text);

      // A failure has to reach the UI. Silently staying at rest reads as an unresponsive button,
      // and the reader goes on to paste whatever the clipboard held before.
      setStatus(ok ? 'copied' : 'failed');
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setStatus('idle'), resetAfterMs);

      return ok;
    },
    [resetAfterMs],
  );

  return { status, copied: status === 'copied', failed: status === 'failed', copy };
}

async function writeText(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Permission denied or a non-secure context — fall through to the legacy path.
    }
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  // Kept off-screen rather than hidden: a display:none element cannot be selected.
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();

  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
}
